import { useEffect } from 'react';
import { AnalyzerPage } from './pages/AnalyzerPage';
import { BestCanBusTools } from './pages/BestCanBusTools';
import { CanBusDecoderGuide } from './pages/CanBusDecoderGuide';
import { DashboardPage } from './pages/DashboardPage';
import { DecodePage } from './pages/DecodePage';
import { DocsPage } from './pages/DocsPage';
import { HomePage } from './pages/HomePage';
import { LivePage } from './pages/LivePage';
import { SmartCANVsAlternatives } from './pages/SmartCANVsAlternatives';
import { WaitlistPage } from './pages/WaitlistPage';
import { WhatIsSmartCAN } from './pages/WhatIsSmartCAN';
import { marketplaceUrl, socialLinks } from './content';
import { trackPageView } from './lib/analytics';
import { applyRouteSeo } from './seo';

const navItems = [
  { href: '/', label: 'Home' },
  { href: '/analyzer', label: 'Analyzer' },
  { href: '/live', label: 'Live' },
  { href: '/decode', label: 'Decode' },
  { href: '/what-is-smartcan', label: 'Guide' },
  { href: '/waitlist', label: 'Waitlist' },
  { href: '/docs', label: 'Docs' },
  { href: marketplaceUrl, label: 'Marketplace', external: true }
];

const brandLogo = '/ameriuse-smartcan-logo.png';

function SocialIcon({ icon }: { icon: string }) {
  if (icon === 'linkedin') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5ZM.35 8.02h4.3V23H.35V8.02ZM8 8.02h4.12v2.05h.06c.57-1.08 1.98-2.22 4.08-2.22 4.36 0 5.17 2.87 5.17 6.6V23h-4.29v-7.57c0-1.8-.03-4.13-2.51-4.13-2.52 0-2.9 1.97-2.9 4V23H8V8.02Z" />
      </svg>
    );
  }

  if (icon === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="17.5" cy="6.5" r="1.25" fill="currentColor" />
      </svg>
    );
  }

  if (icon === 'github') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M12 .9C5.92.9 1 5.82 1 11.9c0 4.86 3.15 8.98 7.52 10.44.55.1.75-.24.75-.53v-1.88c-3.06.66-3.7-1.32-3.7-1.32-.5-1.27-1.22-1.61-1.22-1.61-.99-.68.08-.67.08-.67 1.1.08 1.68 1.13 1.68 1.13.98 1.67 2.56 1.19 3.18.91.1-.71.38-1.19.7-1.46-2.44-.28-5.01-1.22-5.01-5.43 0-1.2.43-2.18 1.13-2.95-.11-.28-.49-1.4.11-2.91 0 0 .92-.3 3.02 1.13.88-.24 1.82-.37 2.75-.37.93 0 1.87.13 2.75.37 2.1-1.43 3.02-1.13 3.02-1.13.6 1.51.22 2.63.11 2.91.7.77 1.13 1.75 1.13 2.95 0 4.22-2.57 5.15-5.02 5.42.39.34.74 1.01.74 2.04v3.02c0 .29.2.64.76.53A11.02 11.02 0 0 0 23 11.9C23 5.82 18.08.9 12 .9Z" />
      </svg>
    );
  }

  if (icon === 'facebook') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M14.2 8.45V6.8c0-.8.54-.99.91-.99h2.31V2.02L14.24 2C10.7 2 9.9 4.65 9.9 6.35v2.1H7v3.9h2.9V22h4.3v-9.65h2.9l.38-3.9H14.2Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h10v10H7z" />
      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5h6v6M12 12l7-7" />
    </svg>
  );
}

function getRoute() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';

  if (path === '/') return <HomePage />;
  if (path === '/analyzer') return <AnalyzerPage />;
  if (path === '/live') return <LivePage />;
  if (path === '/decode') return <DecodePage />;
  if (path === '/what-is-smartcan') return <WhatIsSmartCAN />;
  if (path === '/best-can-bus-tools') return <BestCanBusTools />;
  if (path === '/smartcan-vs-alternatives') return <SmartCANVsAlternatives />;
  if (path === '/can-bus-decoder-guide') return <CanBusDecoderGuide />;
  if (path === '/dashboard') return <DashboardPage />;
  if (path === '/waitlist') return <WaitlistPage />;
  if (path === '/docs' || path.startsWith('/docs/')) return <DocsPage path={path} />;
  if (path === '/marketplace') {
    return (
      <main className="section page-shell">
        <div className="empty-state">
          <p className="eyebrow">VS Code Marketplace</p>
          <h1>SmartCAN Pro Extension</h1>
          <p>Install the SmartCAN VS Code Extension from the Visual Studio Marketplace for USB high-rate live monitoring and capture inspection.</p>
          <a className="button primary" href={marketplaceUrl} target="_blank" rel="noopener noreferrer">Open Marketplace</a>
        </div>
      </main>
    );
  }

  return (
    <main className="section page-shell">
      <div className="empty-state">
        <p className="eyebrow">404</p>
        <h1>Page not found</h1>
        <p>The SmartCAN Pro page you requested does not exist.</p>
        <a className="button primary" href="/">Back to SmartCAN Pro</a>
      </div>
    </main>
  );
}

function useRevealOnScroll(route: string) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = Array.from(document.querySelectorAll<HTMLElement>('.reveal'));

    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      targets.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    targets.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [route]);
}

export default function App() {
  const route = window.location.pathname.replace(/\/$/, '') || '/';

  useEffect(() => {
    applyRouteSeo(window.location.pathname);
    trackPageView(route);
  }, [route]);

  useRevealOnScroll(route);

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href="/" aria-label="SmartCAN Pro by Ameriuse home">
          <span className="brand-mark">
            <img className="brand-logo" src={brandLogo} alt="SmartCAN Pro by Ameriuse logo" />
          </span>
          <span>
            <strong>SmartCAN Pro</strong>
            <small>by Ameriuse</small>
          </span>
        </a>
        <nav className="site-nav" aria-label="Primary navigation">
          {navItems.map((item) => (
            <a
              key={item.href}
              className={!item.external && (route === item.href || route.startsWith(`${item.href}/`)) ? 'active' : ''}
              href={item.href}
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener noreferrer' : undefined}
            >
              {item.label}
            </a>
          ))}
        </nav>
        <a className="header-action" href="/live">Live</a>
      </header>
      {getRoute()}
      <footer className="site-footer">
        <div className="footer-brand">
          <a className="footer-brand-title" href="/" aria-label="SmartCAN Pro by Ameriuse home">
            <span className="footer-mark">
              <img className="footer-logo" src={brandLogo} alt="SmartCAN Pro by Ameriuse logo" />
            </span>
            <span>
              <strong>SmartCAN Pro</strong>
              <small>by Ameriuse</small>
            </span>
          </a>
          <span>Capture, analyze, and decode CAN data across hardware, web, VS Code, and Python.</span>
          <span className="footer-privacy-note">SmartCAN may collect anonymous product usage analytics to improve the app. Raw CAN files stay in your browser and are not uploaded.</span>
          <div className="social-links" aria-label="Ameriuse and SmartCAN social links">
            {socialLinks.filter((item) => item.url).map((item) => (
              <a key={item.label} href={item.url} target="_blank" rel="noopener noreferrer" aria-label={item.ariaLabel} title={item.label}>
                <SocialIcon icon={item.icon} />
              </a>
            ))}
          </div>
        </div>
        <div className="footer-links">
          <a href="/dashboard">Device Dashboard</a>
          <a href="/live">Web Live Mode</a>
          <a href="/decode">Community Decode</a>
          <a href="/what-is-smartcan">What is SmartCAN?</a>
          <a href="/best-can-bus-tools">Best CAN Bus Tools</a>
          <a href="/can-bus-decoder-guide">CAN Decoder Guide</a>
          <a href="/marketplace">Marketplace</a>
          <a href="/docs/troubleshooting">Troubleshooting</a>
          <a href="mailto:support@ameriuse.com">support@ameriuse.com</a>
        </div>
      </footer>
    </div>
  );
}
